/**
 * useAutismPrefillSync.js
 *
 * Hook that synchronises autism form answers to the actor's assessmentStages/history
 * document via the prefill pipeline. Watches for form instance changes and triggers
 * a debounced prefill write after each save stabilises.
 *
 * Design rationale: Rather than modifying useModularFormInstances (shared infra),
 * this hook observes form instance state and independently manages prefill writes.
 * This keeps the form-saving logic unchanged while adding prefill as a side-effect.
 *
 * The hook is conditionally activated only for forms that contain prefill metadata,
 * avoiding unnecessary Firestore reads/writes for rating scale forms (which have no
 * prefill targets).
 *
 * Atomicity note: Unlike ADHD (where prefill is written in the same transaction as
 * the form save), this fires as a separate debounced write after the answer save
 * stabilises. The 800ms debounce (down from the initial 2000ms) minimises the window
 * during which a clinician viewing History might see stale prefill values. A full
 * transactional approach would require restructuring the shared useModularFormInstances
 * hook, which is not warranted given the acceptable latency at this debounce interval.
 */
import { useCallback, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { runAutismPrefill, formHasPrefillMetadata } from '../functions/AutismPrefillFunctions';
import debounce from 'lodash.debounce';

const PREFILL_DEBOUNCE_MS = 800;

// Delay before the single retry when a prefill write fails. Long enough for a
// transient network blip or Firestore contention to clear, short enough that
// the retry still lands while the user is realistically on the page.
const PREFILL_RETRY_DELAY_MS = 1500;

/**
 * @param {object} options
 * @param {boolean} options.enabled - Whether prefill should run (form has metadata + module is autism)
 * @param {string} options.formKey - Current form key
 * @param {object} options.formInstance - Current form instance (formDefinition + answers)
 * @param {string} options.actorType - "patient" or "informant"
 * @param {string} options.actorUid - The auth uid of the current user
 * @param {string|null} options.patientId - The patient uid (same as actorUid for patients, different for informants)
 */
export function useAutismPrefillSync({
  enabled,
  formKey,
  formInstance,
  actorType,
  actorUid,
  patientId,
}) {
  const prefillRunnerRef = useRef(null);
  const latestFormInstanceRef = useRef(formInstance);
  latestFormInstanceRef.current = formInstance;

  /**
   * Resolves the Firestore DocumentReference for the actor's assessmentStages/history doc.
   */
  const getHistoryStageRef = useCallback(() => {
    if (!actorUid || !patientId) return null;

    if (actorType === 'patient') {
      return doc(db, 'patients', actorUid, 'adult_autism', 'assessment_001', 'assessmentStages', 'history');
    }

    // Informant path: informants/{informantUid}/patients/{patientUid}/adult_autism/assessment_001/assessmentStages/history
    return doc(db, 'informants', actorUid, 'patients', patientId, 'adult_autism', 'assessment_001', 'assessmentStages', 'history');
  }, [actorType, actorUid, patientId]);

  /**
   * Core prefill execution. Fetches the history stage's current data, then runs
   * the prefill pipeline with the latest form answers.
   *
   * A failed attempt (network blip, Firestore contention, rules hiccup) is
   * retried ONCE after a short delay. Without the retry, a single transient
   * failure silently loses the prefill write — the answers never reach the
   * clinician proforma or the LLM report inputs, and nothing ever re-attempts
   * the sync unless the actor edits the form again.
   */
  const executePrefill = useCallback(async () => {
    const historyRef = getHistoryStageRef();
    if (!historyRef || !latestFormInstanceRef.current) return;

    const attempt = async () => {
      const historySnap = await getDoc(historyRef);
      if (!historySnap.exists()) return;

      const historyDoc = historySnap.data();
      const historyData = historyDoc?.data;
      if (!historyData) return;

      const prefillField = actorType === 'patient' ? 'patientPrefill' : 'informantPrefill';
      const timestampField = actorType === 'patient'
        ? 'patientPrefillLastUpdatedAt'
        : 'informantPrefillLastUpdatedAt';

      // latestFormInstanceRef is read inside the attempt so the retry uses
      // the freshest answers rather than a stale closure.
      await runAutismPrefill(
        formKey,
        latestFormInstanceRef.current,
        historyData,
        historyRef,
        prefillField,
        timestampField,
      );
    };

    try {
      await attempt();
    } catch (err) {
      console.error('[useAutismPrefillSync] Prefill execution failed, retrying once:', err);
      try {
        await new Promise((resolve) => setTimeout(resolve, PREFILL_RETRY_DELAY_MS));
        await attempt();
      } catch (retryErr) {
        console.error('[useAutismPrefillSync] Prefill retry failed:', retryErr);
      }
    }
  }, [formKey, actorType, getHistoryStageRef]);

  // Create/update the debounced prefill runner
  useEffect(() => {
    if (!enabled) {
      prefillRunnerRef.current?.cancel?.();
      prefillRunnerRef.current = null;
      return;
    }

    prefillRunnerRef.current = debounce(executePrefill, PREFILL_DEBOUNCE_MS);

    return () => {
      prefillRunnerRef.current?.cancel?.();
    };
  }, [enabled, executePrefill]);

  // Trigger prefill whenever form instance answers change
  const prevAnswersRef = useRef(null);
  useEffect(() => {
    if (!enabled || !formInstance?.answers) return;

    // Shallow reference check — answers object reference changes on each save
    if (prevAnswersRef.current === formInstance.answers) return;
    prevAnswersRef.current = formInstance.answers;

    prefillRunnerRef.current?.();
  }, [enabled, formInstance?.answers]);

  // Flush on unmount (user navigates away)
  useEffect(() => {
    return () => {
      prefillRunnerRef.current?.flush?.();
    };
  }, []);

  // Flush when the page is being hidden or unloaded. React unmount cleanup
  // does NOT run when the user closes the tab, kills the browser, or switches
  // away on mobile — so an edit made within the debounce window (800ms) would
  // otherwise be silently lost, leaving stored prefill stale. pagehide covers
  // tab close/navigation; visibilitychange→hidden covers mobile app switching
  // (where pagehide may never fire before the process is frozen/killed).
  // Note the flush is best-effort: the async Firestore write may still be cut
  // short by the browser, but flushing here maximises the chance it lands.
  useEffect(() => {
    if (!enabled) return undefined;

    const flushPending = () => {
      prefillRunnerRef.current?.flush?.();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushPending();
    };

    window.addEventListener('pagehide', flushPending);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', flushPending);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);
}
